import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import 'bootstrap/dist/css/bootstrap.min.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import useAuth from '../context/useAuth';

const UserProfile = () => {
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: '',
        isOTPRequired: false,
        last_login: '',
        status: '',
        memberId: '',
        role: '',
        id: '',
    });
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const { updateResultsPerPage } = useAuth();

    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [showPasswords, setShowPasswords] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPasswords(!showPasswords);
    };

    useEffect(() => {
        // Fetch user profile data when the component mounts
        api.get('/api/user-profile')
            .then(response => {
                setProfile(response.data);
            })
            .catch(error => {
                console.error('Error fetching profile data', error);
                setErrorMessage('Error fetching profile data.');
                setSuccessMessage('');
            });
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfile((prevProfile) => ({
            ...prevProfile,
            [name]: value,
        }));
    };


    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswords((prev) => ({ ...prev, [name]: value }));
    };


    const handleProfileSubmit = (e) => {
        e.preventDefault();

        // Validate username
        if (profile.name.length < 6 || /\s/.test(profile.name)) {
            setErrorMessage('Username must be at least 6 characters long and cannot contain spaces.');
            setSuccessMessage('');
            return;
        }
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(profile.email)) {
            setErrorMessage('Please enter a valid email address.');
            setSuccessMessage('');
            return;
        }

        // Update user profile
        api.put('/api/user-profile', profile)
            .then(response => {
                setSuccessMessage('Profile updated successfully!');
                updateResultsPerPage(profile.resultsPerPage); // Update the context here
                setErrorMessage(''); // Clear any previous errors
            })
            .catch(error => {
                // Extract error message from the response
                const message = error.response?.data?.message || 'Error updating profile.';
                setErrorMessage(message);
                console.error('Error details:', error); // Optional: Log error details for debugging
                setSuccessMessage('');
            });
    };

    const handlePasswordSubmit = (e) => {
        e.preventDefault();

        // Check for password validity
        const { currentPassword, newPassword, confirmPassword } = passwords;

        if (newPassword.length < 10) {
            setErrorMessage('New password must be at least 10 characters long.');
            setSuccessMessage('');
            return;
        }

        if (/\s/.test(newPassword) || /\s/.test(currentPassword)) {
            setErrorMessage('Passwords cannot contain spaces.');
            setSuccessMessage('');
            return;
        }

        if (newPassword === currentPassword) {
            setErrorMessage('New password cannot be the same as the current password.');
            setSuccessMessage('');
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMessage('New passwords do not match.');
            setSuccessMessage('');
            return;
        }

        // Update user password
        api.put('/api/change-password', passwords)
            .then(response => {
                setSuccessMessage('Password updated successfully!');
                setErrorMessage(''); // Clear any previous errors
                setShowPasswordFields(false);
            })
            .catch(error => {
                setErrorMessage('Error updating password.');
                setSuccessMessage('');
            });
    };

    return (
        <div className="container mt-5 small-font">
            <h2 className='small-font'><strong>User Profile</strong></h2>
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            <form onSubmit={handleProfileSubmit} className="mb-4 small-font">
                <div className="mb-3 row">
                    <label htmlFor="name" className="col-form-label col-sm-4">Username: <span className="text-danger">* Minimum 6 characters</span></label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={profile.name}
                            onChange={handleInputChange}
                        />

                    </div>
                </div>
                <div className="mb-3 row">
                    <label htmlFor="email" className="col-form-label col-sm-4">Email:<span className="text-danger">*</span></label>
                    <div className="col-sm-8">
                        <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={profile.email}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                {/* Dropdown for Results Per Page */}
                <div className="mb-3 row">
                    <label htmlFor="resultsPerPage" className="col-form-label col-sm-4">
                        Results Per Page:<span className="text-danger">*</span>
                    </label>
                    <div className="col-sm-8">
                        <select
                            className="form-control"
                            name="resultsPerPage"
                            value={profile.resultsPerPage || 10}
                            onChange={handleInputChange}
                        >
                            <option value={10}>10</option>
                            <option value={15}>15</option>
                            <option value={20}>20</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={75}>75</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
                <div className="mb-3 row">
                    <label htmlFor="phone" className="col-form-label col-sm-4">
                        Phone:<span className="text-danger">*</span>
                    </label>
                    <div className="col-sm-8">
                        <PhoneInput
                            country={"us"} // You can set the default country here
                            value={profile.phone}
                            onChange={(phone) => handleInputChange({ target: { name: 'phone', value: phone } })}
                            inputProps={{
                                name: 'phone',
                                required: true,
                                className: 'form-control',
                            }}
                        />
                    </div>
                </div>
                <div className="mb-3 form-check row">
                    <div className="col-sm-4"></div> {/* Empty column for alignment */}
                    <div className="col-sm-8">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            name="isOTPRequired"
                            checked={profile.isOTPRequired}
                            onChange={() => setProfile({ ...profile, isOTPRequired: !profile.isOTPRequired })}
                        />
                        <label className="form-check-label">OTP Required</label>
                    </div>
                </div>
                <div className="mb-3" style={{ opacity: 0.5, border: '2px solid gray', padding: '10px', borderRadius: '5px' }}>
                    <div className="row">
                        <div className="col-md-6 d-flex align-items-center">
                            <p className="mb-0"><strong>Status:</strong> {profile.isActive ? 'Active' : 'N/A'}</p>
                        </div>
                        <div className="col-md-6 d-flex align-items-center">
                            <p className="mb-0"><strong>Member ID:</strong> {profile.memberId ? profile.memberId : 'N/A'}</p>
                        </div>
                        <div className="col-md-6 d-flex align-items-center">
                            <p className="mb-0"><strong>Role:</strong> {profile.role}</p>
                        </div>
                        <div className="col-md-6 d-flex align-items-center">
                            <p className="mb-0"><strong>User ID:</strong> {profile.id}</p>
                        </div>
                        <div className="col-md-6 d-flex align-items-center">
                            <p className="mb-0"><strong>Last Login:</strong> {profile.last_login ? new Date(profile.last_login).toLocaleString() : 'Never'}</p>
                        </div>
                    </div>
                </div>
                <button type="submit" className="btn btn-primary small-font">Update Profile</button>
            </form>
            <button
                className="btn btn-primary mb-3 small-font"
                onClick={() => setShowPasswordFields((prev) => !prev)}
            >
                {showPasswordFields ? "Hide Password Fields" : "Change Password"}
            </button>
            {showPasswordFields && (
                <form onSubmit={handlePasswordSubmit} className="small-font">
                    <div className="mb-3 row">
                        <label
                            htmlFor="currentPassword"
                            className="col-sm-4 col-form-label mb-0"
                        >
                            Current Password:<span className="text-danger">*</span>
                        </label>
                        <div className="col-sm-8">
                            <input
                                type={showPasswords ? "text" : "password"}
                                className="form-control"
                                name="currentPassword"
                                value={passwords.currentPassword}
                                onChange={handlePasswordChange}
                            />
                        </div>
                    </div>
                    <div className="mb-3 row">
                        <label
                            htmlFor="newPassword"
                            className="col-sm-4 col-form-label mb-0"
                        >
                            New Password:<span className="text-danger">* Minimum 10 characters</span>
                        </label>
                        <div className="col-sm-8">
                            <input
                                type={showPasswords ? "text" : "password"}
                                className="form-control"
                                name="newPassword"
                                value={passwords.newPassword}
                                onChange={handlePasswordChange}
                            />
                        </div>
                    </div>
                    <div className="mb-3 row">
                        <label
                            htmlFor="confirmPassword"
                            className="col-sm-4 col-form-label mb-0"
                        >
                            Confirm New Password:<span className="text-danger">*</span>
                        </label>
                        <div className="col-sm-8">
                            <input
                                type={showPasswords ? "text" : "password"}
                                className="form-control"
                                name="confirmPassword"
                                value={passwords.confirmPassword}
                                onChange={handlePasswordChange}
                            />
                        </div>
                    </div>
                    <div className="form-check mb-3">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="showPasswords"
                            checked={showPasswords}
                            onChange={togglePasswordVisibility}
                        />
                        <label className="form-check-label" htmlFor="showPasswords">
                            Show Passwords
                        </label>
                    </div>
                    <button type="submit" className="btn btn-primary small-font">Change Password</button>
                </form>
            )}
        </div>
    );
};

export default UserProfile;